// ui/src/components/shared/PayoutExplanationPanel.jsx
/**
 * S993 REWRITE — consuming earning_sections[] canonical contract.
 *
 * DESIGN PRINCIPLES (SME consultation S993):
 *   1. earning_sections[].total_base is AUTHORITATIVE. Never overridden.
 *   2. Router owns narrative. JSX renders verbatim. No inference.
 *   3. RTD aggregated by revenue type. Pre-RTD total hidden from rep.
 *   4. Ramp is explicit proof step when operative.
 *   5. Multi-BU quota shown as aggregate + grain count context.
 *
 * SECTION ORDER:
 *   Individual:    Summary → Reconciliation → Your Production → Your Target → Plan Details
 *   Player-Coach:  Summary → Reconciliation → Your Production → Team Production → Your Target → Plan Details
 *   Rollup/Leadership: Summary → Reconciliation → Team Production → Your Target → Plan Details
 *
 * DATA SOURCE: GET /api/v1/payout-summaries/{id} (EP 12.2)
 * PEP reads: earning_sections[], reconciliation_items[], earning_model,
 *            calculation_trace (OTE strip only), planContext, earning_sections_target.
 * Legacy fields (components[], proof_steps[], earning_sources[]) not consumed here.
 */

import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useResizablePanel } from '../../hooks/useResizablePanel.js';

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function fmtCurrency(value, currencyCode) {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return '—';
  if (!currencyCode) return new Intl.NumberFormat(undefined).format(n);
  return new Intl.NumberFormat(undefined, {
    style: 'currency', currency: currencyCode, currencyDisplay: 'narrowSymbol',
  }).format(n);
}

function fmtPct(value, decimals = 2) {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'string' ? parseFloat(value) : value;
  if (!Number.isFinite(n)) return '—';
  // All router values are decimal ratios (1.0 = 100%). Always multiply by 100.
  // The previous n > 2 heuristic broke for attainment ≥ 200% (e.g. 2.3532 displayed
  // as 2.35% instead of 235.32%). Confirmed: every fmtPct caller receives a router
  // value from _rate() or _rate6() which always emits decimal ratios.
  return `${(n * 100).toFixed(decimals)}%`;
}

function fmtRate(rateStr) {
  const n = parseFloat(rateStr);
  if (!Number.isFinite(n)) return '—';
  return `${(n * 100).toFixed(0)}%`;
}

function fmtDate(isoStr) {
  if (!isoStr) return null;
  try {
    return new Date(isoStr).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch { return isoStr; }
}

function numeric(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function currencyTolerance(currencyCode, rowCount = 1) {
  if (!currencyCode) return Number.EPSILON;
  try {
    const opts = new Intl.NumberFormat(undefined, {
      style: 'currency', currency: currencyCode,
    }).resolvedOptions();
    const digits = opts.maximumFractionDigits ?? 2;
    const unit = 10 ** (-digits);
    return unit * Math.max(1, rowCount) * 0.5;
  } catch { return 0.005 * Math.max(1, rowCount); }
}

// ---------------------------------------------------------------------------
// usePayoutDetail
// ---------------------------------------------------------------------------

function usePayoutDetail(payoutSummaryId) {
  const [detail, setDetail]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!payoutSummaryId) { setDetail(null); return; }
    let cancelled = false;
    setLoading(true); setError(null); setDetail(null);
    fetch(`/api/v1/payout-summaries/${encodeURIComponent(payoutSummaryId)}`, {
      credentials: 'include',
    })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(data => { if (!cancelled) setDetail(data); })
      .catch(e  => { if (!cancelled) setError(e); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [payoutSummaryId]);

  return { detail, loading, error };
}

// ---------------------------------------------------------------------------
// Rate state display
// ---------------------------------------------------------------------------

const RATE_STATE_LABELS = {
  accelerator:    'Accelerator',
  standard:       'Standard rate',
  below_target:   'Below-target rate',
  not_achieved:   'Not achieved',
  not_applicable: 'Not applicable',
};

// ---------------------------------------------------------------------------
// Small layout atoms
// ---------------------------------------------------------------------------

function SectionEyebrow({ children }) {
  return <p className="pep__eyebrow">{children}</p>;
}

function CollapsibleSection({ eyebrow, children, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  return (
    <section className="pep__section pep__section--collapsible">
      <button type="button" className="pep__section-toggle"
        onClick={() => setExpanded(v => !v)} aria-expanded={expanded}>
        <SectionEyebrow>{eyebrow}</SectionEyebrow>
        <span className="pep__section-chevron">{expanded ? '▼' : '▶'}</span>
      </button>
      {expanded && <div className="pep__section-body">{children}</div>}
    </section>
  );
}

function MetricTile({ label, value, sub }) {
  return (
    <div className="pep__metric">
      <span className="pep__metric-label">{label}</span>
      <strong className="pep__metric-value">{value}</strong>
      {sub && <span className="pep__metric-sub">{sub}</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// OTE anchor strip
// ---------------------------------------------------------------------------

function OteAnchorStrip({ detail, displayAmount, currency, earningSections, earningModel }) {
  const oteRaw    = detail?.calculation_trace?.ote_midpoint;
  const vpRaw     = detail?.calculation_trace?.variable_pct;
  const oteBase   = numeric(oteRaw);
  const varPct    = numeric(vpRaw);

  if (oteBase == null || varPct == null || varPct === 0) return null;

  const oteDisplay    = displayAmount(oteBase);
  const varTargetBase = oteBase * varPct;
  const varTargetDisp = displayAmount(varTargetBase);

  const isPureRollup = earningModel === 'rollup' || earningModel === 'leadership_rollup';
  const isPlayerCoach = earningModel === 'player_coach';

  const varTargetLabel = isPureRollup ? 'Team variable target' : 'Variable target';

  // Weight note for player-coach
  let weightNote = null;
  if (isPlayerCoach && earningSections) {
    const yp = earningSections.find(s => s.section_key === 'your_production');
    const tp = earningSections.find(s => s.section_key === 'team_production');
    const ypW = numeric(yp?.target_weight);
    const tpW = numeric(tp?.target_weight);
    if (ypW != null && tpW != null) {
      weightNote = `Your Production ${fmtPct(ypW)} · Team Production ${fmtPct(tpW)}`;
    }
  }

  return (
    <div className="pep__ote-strip">
      <div className="pep__ote-strip-cell">
        <span className="pep__ote-strip-label">On-Target Earnings (OTE)</span>
        <strong className="pep__ote-strip-value">{fmtCurrency(oteDisplay, currency)}</strong>
      </div>
      <span className="pep__ote-strip-op">×</span>
      <div className="pep__ote-strip-cell">
        <span className="pep__ote-strip-label">Variable %</span>
        <strong className="pep__ote-strip-value">{fmtPct(varPct)}</strong>
      </div>
      <span className="pep__ote-strip-op">=</span>
      <div className="pep__ote-strip-cell">
        <span className="pep__ote-strip-label">{varTargetLabel}</span>
        <strong className="pep__ote-strip-value">{fmtCurrency(varTargetDisp, currency)}</strong>
        {weightNote && <span className="pep__ote-strip-note">{weightNote}</span>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SECTION 1 — Performance tiles
// ---------------------------------------------------------------------------

function PerformanceTiles({ detail, displayAmount, currency, baseCurrencyCode, earningModel,
                            toLocal, viewCurrency }) {
  const trace = detail?.calculation_trace ?? {};
  const perf  = detail?.performance_summary ?? {};

  if (earningModel === 'player_coach') {
    const yp = perf.your_production ?? {};
    const tp = perf.team_production ?? {};
    const tpIsMultiCcy = tp.currency_count > 1;
    // Team figures: always computed in base (USD). Apply toLocal when on local tab.
    const tpDisplayAmt = (base) => {
      const n = numeric(base);
      if (n == null) return null;
      return viewCurrency === 'local' ? toLocal(n) : n;
    };
    const tpCcy = viewCurrency === 'local' ? currency : baseCurrencyCode;
    return (
      <div className="pep__dual-performance">
        {[
          ['YOUR PRODUCTION', yp, false],
          ['TEAM PRODUCTION', tp, true],
        ].map(([title, p, isTeam]) => (
          <div key={title} className="pep__performance-card">
            <div className="pep__performance-card-title">
              <span>{title}</span>
              {isTeam && (
                <small>{tpCcy} · {viewCurrency === 'local' ? 'Local' : 'Company'} performance</small>
              )}
            </div>
            <div className="pep__performance-four">
              <MetricTile
                label={isTeam ? 'Team Target' : 'Quota'}
                value={p.quota_base != null
                  ? fmtCurrency(isTeam ? tpDisplayAmt(p.quota_base) : displayAmount(numeric(p.quota_base)),
                      isTeam ? tpCcy : currency)
                  : 'Not available'}
              />
              <MetricTile
                label={isTeam ? 'Eligible Production' : 'Credited Production'}
                value={p.credit_base != null
                  ? fmtCurrency(isTeam ? tpDisplayAmt(p.credit_base) : displayAmount(numeric(p.credit_base)),
                      isTeam ? tpCcy : currency)
                  : 'Not available'}
              />
              <MetricTile label="Attainment"
                value={p.attainment != null ? fmtPct(p.attainment) : 'Not available'} />
              <MetricTile
                label={!isTeam && p.has_rtd ? 'Adj Payout Rate' : 'Payout Rate'}
                value={p.payout_rate != null ? fmtRate(p.payout_rate) : 'Not applicable'}
                sub={p.rate_state && p.rate_state !== 'not_applicable'
                  ? (RATE_STATE_LABELS[p.rate_state] ?? p.rate_state) : null} />
            </div>
            {isTeam && tpIsMultiCcy && (
              <p className="pep__team-basis-hq-note">
                Your team's credits span multiple currencies.
              </p>
            )}
          </div>
        ))}
      </div>
    );
  }

  if (earningModel === 'rollup' || earningModel === 'leadership_rollup') {
    const tp = perf.team_production ?? {};
    const tpIsMultiCcy = tp.currency_count > 1;
    const tpDisplayAmt = (base) => {
      const n = numeric(base);
      if (n == null) return null;
      return viewCurrency === 'local' ? toLocal(n) : n;
    };
    const tpCcy = viewCurrency === 'local' ? currency : baseCurrencyCode;
    return (
      <div className="pep__performance-card">
        <div className="pep__performance-card-title">
          <span>TEAM PRODUCTION</span>
          <small>{tpCcy} · {viewCurrency === 'local' ? 'Local' : 'Company'} performance</small>
        </div>
        <div className="pep__performance-four">
          <MetricTile label="Team Target"
            value={tp.quota_base != null ? fmtCurrency(tpDisplayAmt(tp.quota_base), tpCcy) : 'Not available'} />
          <MetricTile label="Eligible Production"
            value={tp.credit_base != null ? fmtCurrency(tpDisplayAmt(tp.credit_base), tpCcy) : 'Not available'} />
          <MetricTile label="Attainment"
            value={tp.attainment != null ? fmtPct(tp.attainment) : 'Not available'} />
          <MetricTile label="Payout Rate"
            value={tp.payout_rate != null ? fmtRate(tp.payout_rate) : 'Not applicable'}
            sub={tp.rate_state && tp.rate_state !== 'not_applicable'
              ? (RATE_STATE_LABELS[tp.rate_state] ?? tp.rate_state) : null} />
        </div>
        {tpIsMultiCcy && (
          <p className="pep__team-basis-hq-note">
            Your team's credits span multiple currencies.
          </p>
        )}
      </div>
    );
  }

  // Individual
  // PEP-RATE-DISPLAY-RULE (S994/S995): when RTD is present, the tile must show
  // the effective realised rate (arr_payout / production_target) labelled
  // "Adj Payout Rate". The band rate from component_rate_states is still shown
  // in the proof chain but must NOT appear in the headline tile when RTD present.
  const yp = perf.your_production ?? {};
  if (yp.has_rtd && yp.payout_rate != null) {
    return (
      <div className="pep__summary-grid">
        <MetricTile label="Quota"
          value={fmtCurrency(displayAmount(numeric(detail.quota_reporting)), currency)} />
        <MetricTile label="Credited ARR"
          value={fmtCurrency(displayAmount(numeric(detail.credit_total_reporting)), currency)} />
        <MetricTile label="Attainment"
          value={detail.attainment_pct != null ? fmtPct(detail.attainment_pct) : 'Not available'} />
        <MetricTile label="Adj Payout Rate"
          value={fmtRate(yp.payout_rate)}
          sub={yp.rate_state && yp.rate_state !== 'not_applicable'
            ? (RATE_STATE_LABELS[yp.rate_state] ?? yp.rate_state) : null} />
      </div>
    );
  }
  const crs = (trace.component_rate_states || []).filter(s => s.rate_state !== 'not_applicable');
  let rateNode;
  if (crs.length === 0) {
    rateNode = <MetricTile label="Payout Rate" value="Not applicable" />;
  } else if (crs.length === 1) {
    rateNode = (
      <MetricTile label="Payout Rate"
        value={fmtRate(crs[0].rate)}
        sub={RATE_STATE_LABELS[crs[0].rate_state] ?? crs[0].rate_state} />
    );
  } else if (crs.length === 2) {
    rateNode = (
      <div className="pep__metric pep__metric--rates">
        <span className="pep__metric-label">Payout Rates</span>
        {crs.map((s, i) => (
          <div key={i} className="pep__rate-tile-row">
            <span className="pep__rate-tile-component">{s.component}</span>
            <span className="pep__rate-tile-rate">{fmtRate(s.rate)}</span>
            <span className="pep__rate-tile-state">{RATE_STATE_LABELS[s.rate_state] ?? s.rate_state}</span>
          </div>
        ))}
      </div>
    );
  } else {
    rateNode = (
      <MetricTile label="Payout Rates"
        value={`${crs.length} component rates`}
        sub="See calculations below" />
    );
  }

  return (
    <div className="pep__summary-grid">
      <MetricTile label="Quota"
        value={fmtCurrency(displayAmount(numeric(detail.quota_reporting)), currency)} />
      <MetricTile label="Credited ARR"
        value={fmtCurrency(displayAmount(numeric(detail.credit_total_reporting)), currency)} />
      <MetricTile label="Attainment"
        value={detail.attainment_pct != null ? fmtPct(detail.attainment_pct) : 'Not available'} />
      {rateNode}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SECTION 2 — Reconciliation
// ---------------------------------------------------------------------------

function ReconciliationSection({ detail, totalPayout, currency, toLocal, viewCurrency }) {
  const items = detail?.reconciliation_items ?? [];
  const getAmount = item =>
    viewCurrency === 'base'
      ? numeric(item.base_amount)
      : (numeric(item.local_amount) ?? toLocal(numeric(item.base_amount)));

  const groups = [
    ['Your Production',              items.filter(x => x.group === 'your_production')],
    ['Team Production',              items.filter(x => x.group === 'team_production')],
    ['Other Earnings & Adjustments', items.filter(x => x.group === 'independent')],
  ];

  const rowSum  = items.reduce((s, x) => s + (getAmount(x) ?? 0), 0);
  const stored  = numeric(totalPayout) ?? 0;
  const residual = stored - rowSum;
  const tol     = currencyTolerance(currency, items.length + 1);
  const roundingOnly     = Math.abs(residual) > 0 && Math.abs(residual) <= tol;
  const materialMismatch = Math.abs(residual) > tol;
  const integrityProblem = detail?.reconciliation_status === 'data_mismatch' || materialMismatch;

  return (
    <section className="pep__section">
      <SectionEyebrow>How Your Payout Adds Up</SectionEyebrow>
      <div className="pep__reconcile">
        {groups.map(([title, rows]) => {
          if (!rows.length) return null;
          return (
            <div key={title} className="pep__bridge-group">
              <div className="pep__bridge-group-title">{title}</div>
              {rows.map(row => (
                <div key={row.key} className="pep__reconcile-row pep__reconcile-row--detail">
                  <span>{row.display_name}</span>
                  <span className="pep__reconcile-amount">
                    {fmtCurrency(getAmount(row), currency)}
                  </span>
                </div>
              ))}
            </div>
          );
        })}
        {roundingOnly && (
          <div className="pep__reconcile-row">
            <span>FX & rounding adjustment</span>
            <span className="pep__reconcile-amount">{fmtCurrency(residual, currency)}</span>
          </div>
        )}
        <div className="pep__reconcile-total">
          <span>Total payout</span>
          <strong>{fmtCurrency(totalPayout, currency)}</strong>
        </div>
        {integrityProblem && (
          <div className="pep__integrity-warning" role="alert">
            <strong>Payout bridge incomplete</strong>
            <span>The earning-source amounts do not reconcile to the stored total payout.</span>
          </div>
        )}
        {!integrityProblem && detail?.calculation_trace?.ote_as_pct_check != null && (
          <div className="pep__reconcile-pct-ote">
            <span>Payout as % of OTE</span>
            <span>{fmtPct(detail.calculation_trace.ote_as_pct_check)}</span>
          </div>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Proof chain renderer — used by all earning sections
// ---------------------------------------------------------------------------

function ProofRow({ step, currency, viewCurrency, toLocal }) {
  const cls = [
    'pep__proof-row',
    step.is_result   ? 'pep__proof-row--result' : '',
    step.is_negative ? 'pep__proof-row--neg'    : '',
    step.is_zero     ? 'pep__proof-row--zero'   : '',
  ].filter(Boolean).join(' ');

  let displayValue = step.value;
  if (step.value_type === 'currency' && step.value != null && step.value !== '') {
    const n = parseFloat(String(step.value).replace(/[^0-9.-]/g, ''));
    if (Number.isFinite(n)) {
      const amt = viewCurrency === 'local' ? toLocal(n) : n;
      if (amt != null) displayValue = fmtCurrency(amt, currency);
    }
  }

  return (
    <div className={cls}>
      <span className="pep__proof-label">
        {step.label}
        {step.note && <span className="pep__proof-note"> {step.note}</span>}
      </span>
      <span className="pep__proof-value">{displayValue}</span>
    </div>
  );
}

function ProofChain({ steps, currency, viewCurrency, toLocal }) {
  if (!steps || !steps.length) return null;
  return (
    <div className="pep__proof">
      {steps.map((step, i) => (
        <ProofRow key={i} step={step} currency={currency}
          viewCurrency={viewCurrency} toLocal={toLocal} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Revenue type section (RTD aggregate)
// ---------------------------------------------------------------------------

function RevenueTypeSection({ rtd, currency, viewCurrency, toLocal }) {
  if (!rtd || !rtd.present) return null;
  return (
    <div className="pep__rtd-section">
      <p className="pep__proof-intro">{rtd.intro}</p>
      <div className="pep__proof">
        {rtd.rows.map((row, i) => {
          const payout = numeric(row.payout);
          const displayPayout = viewCurrency === 'local' ? toLocal(payout) : payout;
          return (
            <div key={i} className="pep__proof-row pep__rtd-row">
              <span className="pep__proof-label">
                <span className="pep__rtd-type">{row.display_name}</span>
                <span className="pep__rtd-detail">
                  {fmtCurrency(numeric(row.credited_amount), currency.replace ? currency : undefined)} eligible
                  · {row.deal_count} {row.deal_count === 1 ? 'deal' : 'deals'}
                  · Plan rate {fmtRate(row.rate)}
                </span>
              </span>
              <span className="pep__proof-value">{fmtCurrency(displayPayout, currency)}</span>
            </div>
          );
        })}
        <div className="pep__proof-row pep__proof-row--result">
          <span className="pep__proof-label">= Your production earnings</span>
          <span className="pep__proof-value">
            {fmtCurrency(viewCurrency === 'local' ? toLocal(numeric(rtd.total)) : numeric(rtd.total), currency)}
          </span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SECTION 3 — Earning sections (Your Production / Team Production)
// ---------------------------------------------------------------------------

function PersonalPerformanceBlock({ perf, currency, displayAmount, baseCurrencyCode }) {
  if (!perf) return null;
  const quota  = numeric(perf.quota_base);
  const credit = numeric(perf.credit_base);
  const att    = numeric(perf.attainment);
  return (
    <div className="pep__personal-perf">
      <div className="pep__perf-row-block">
        {quota  != null && <div className="pep__perf-kv"><span>Quota</span><strong>{fmtCurrency(displayAmount(quota), currency)}</strong></div>}
        {credit != null && <div className="pep__perf-kv"><span>Credited production</span><strong>{fmtCurrency(displayAmount(credit), currency)}</strong></div>}
        {att    != null && <div className="pep__perf-kv"><span>Attainment</span><strong>{fmtPct(att)}</strong></div>}
        {perf.payout_rate != null && (
          <div className="pep__perf-kv">
            <span>{perf.has_rtd ? 'Adj payout rate' : 'Payout rate'}</span>
            <strong>
              {fmtRate(perf.payout_rate)}
              {perf.rate_state && perf.rate_state !== 'not_applicable' &&
                <small>{RATE_STATE_LABELS[perf.rate_state] ?? perf.rate_state}</small>}
            </strong>
          </div>
        )}
      </div>
      {perf.quota_grain_count > 1 && (
        <p className="pep__quota-grain-note">
          Quota covers {perf.quota_grain_label || `${perf.quota_grain_count} business units`}
        </p>
      )}
    </div>
  );
}

function TeamPerformanceBlock({ teamPerf, baseCurrencyCode, toLocal, viewCurrency, currency }) {
  if (!teamPerf) return null;
  const eligible       = numeric(teamPerf.eligible_production_base);
  const target         = numeric(teamPerf.target_base);
  const att            = numeric(teamPerf.attainment);
  const isMultiCcy     = teamPerf.currency_count > 1;
  // Display currency follows the active tab — same toLocal() pattern as all other figures.
  const tpCcy          = viewCurrency === 'local' ? currency : baseCurrencyCode;
  const tpAmt          = (base) => base == null ? null
    : viewCurrency === 'local' ? toLocal(base) : base;
  return (
    <div className="pep__team-basis">
      <div className="pep__team-basis-header">
        <span>Team Performance</span>
        <span className="pep__currency-context">
          {tpCcy} · {viewCurrency === 'local' ? 'Local' : 'Company'}
        </span>
      </div>
      <div className="pep__team-basis-metrics">
        {target   != null && (
          <div className="pep__team-basis-row">
            <span>Team target</span>
            <span>{fmtCurrency(tpAmt(target), tpCcy)}</span>
          </div>
        )}
        {eligible != null && (
          <div className="pep__team-basis-row">
            <span>Eligible team production</span>
            <span>{fmtCurrency(tpAmt(eligible), tpCcy)}</span>
          </div>
        )}
        {att != null && (
          <div className="pep__team-basis-row pep__team-basis-row--result">
            <span>Team attainment</span>
            <span>{fmtPct(att)}</span>
          </div>
        )}
        {teamPerf.eligible_position_count != null && (
          <p className="pep__team-basis-summary">
            {teamPerf.eligible_position_count} eligible positions
            {isMultiCcy ? ` · ${teamPerf.currency_count} currencies` : ''}
          </p>
        )}
        {isMultiCcy && (
          <p className="pep__team-basis-hq-note">
            Your team's credits span multiple currencies.
          </p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// QuotaGrainsTable — S998: per-BU quota grain breakdown
// Rendered below performance tiles when quota_grains?.length > 1 (Guard 6).
// Data sourced from arr_grain rows written by engine (AD-S997-ARR-GRAIN-FIELD-CONTRACT).
// Guard 6 is enforced here AND in the router (Guard 5) — double-gated.
// ---------------------------------------------------------------------------
function QuotaGrainsTable({ grains, currency }) {
  const [open, setOpen] = useState(false);
  if (!grains || grains.length <= 1) return null;  // Guard 6
  return (
    <div className="pep__grain-breakdown">
      <button
        className="pep__grain-breakdown-toggle"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <span>Business unit breakdown · {grains.length} units</span>
        <span className="pep__grain-breakdown-chevron">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="pep__grain-breakdown-table">
          <div className="pep__grain-breakdown-header">
            <span>Business Unit</span>
            <span>Quota</span>
            <span>Credited Production</span>
            <span>Attainment</span>
          </div>
          {grains.map(g => (
            <div key={g.bu_code} className="pep__grain-breakdown-row">
              <span className="pep__grain-bu-name">{g.bu_name || g.bu_code}</span>
              <span>{g.quota != null ? fmtCurrency(numeric(g.quota), currency) : '—'}</span>
              <span>{g.credit != null ? fmtCurrency(numeric(g.credit), currency) : '—'}</span>
              <span>{g.attainment != null ? fmtPct(numeric(g.attainment)) : '—'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EarningSection({ section, currency, viewCurrency, toLocal, displayAmount,
                          baseCurrencyCode, localCurrencyCode }) {
  const isPersonal = section.section_type === 'personal_production';
  const isTeam     = section.section_type === 'team_production';

  const totalBase  = numeric(section.total_base);
  const totalLocal = numeric(section.total_local);
  const totalDisplay = viewCurrency === 'local' && totalLocal != null ? totalLocal : totalBase;

  // Currency context separator for team earnings
  const activeContext = viewCurrency === 'local' && localCurrencyCode
    ? `${localCurrencyCode} · Local`
    : `${baseCurrencyCode} · Company`;

  return (
    <section className="pep__section">
      {/* Section header with authoritative total */}
      <div className="pep__component-header">
        <div>
          <SectionEyebrow>{section.display_name}</SectionEyebrow>
          {section.subtitle && <p className="pep__proof-intro">{section.subtitle}</p>}
        </div>
        <strong className="pep__section-total">{fmtCurrency(totalDisplay, currency)}</strong>
      </div>

      {/* Personal performance block */}
      {isPersonal && section.performance && (
        <PersonalPerformanceBlock
          perf={section.performance}
          currency={currency}
          displayAmount={displayAmount}
          baseCurrencyCode={baseCurrencyCode}
        />
      )}

      {/* Per-BU quota grain breakdown — S998: OI-PEP-GRAIN-DETAIL-DISPLAY.
          Only rendered for personal_production sections with quota_grains from the router.
          Guard 6 (length > 1) enforced inside QuotaGrainsTable. */}
      {isPersonal && section.quota_grains?.length > 1 && (
        <QuotaGrainsTable grains={section.quota_grains} currency={currency} />
      )}

      {/* Team performance block — numbers follow active tab currency via toLocal() */}
      {isTeam && section.team_performance && (
        <TeamPerformanceBlock
          teamPerf={section.team_performance}
          baseCurrencyCode={baseCurrencyCode}
          toLocal={toLocal}
          viewCurrency={viewCurrency}
          currency={currency}
        />
      )}

      {/* Currency context separator for team earnings */}
      {isTeam && (
        <div className="pep__currency-transition">
          <span className="pep__currency-transition-label">
            YOUR TEAM EARNINGS · {activeContext}
          </span>
        </div>
      )}

      {/* Proof chain — router-supplied, rendered verbatim */}
      {section.proof_chain && section.proof_chain.length > 0 && (
        <ProofChain
          steps={section.proof_chain}
          currency={currency}
          viewCurrency={viewCurrency}
          toLocal={toLocal}
        />
      )}

      {/* Revenue type section (RTD aggregate) — personal production only */}
      {isPersonal && section.revenue_type_section?.present && (
        <RevenueTypeSection
          rtd={section.revenue_type_section}
          currency={currency}
          viewCurrency={viewCurrency}
          toLocal={toLocal}
        />
      )}

      {/* Adj payout rate — rendered after RTD rows when effective_rate_step present.
          Shows arr_payout / production_target in primary purple, closing the circle. */}
      {isPersonal && section.effective_rate_step && (
        <div className="pep__proof">
          <ProofRow
            step={section.effective_rate_step}
            currency={currency}
            viewCurrency={viewCurrency}
            toLocal={toLocal}
          />
        </div>
      )}

      {/* Terminal total for team sections — only shown when proof_chain does NOT
          already terminate with an is_result step. When _build_team_proof_chain()
          supplies a terminal "= Team Production earnings" row (is_result=true),
          rendering this div would duplicate it (Bug W S994). */}
      {!isPersonal &&
       !section.revenue_type_section?.present &&
       !section.proof_chain?.some(s => s.is_result) && (
        <div className="pep__source-total">
          <span>= {section.display_name} earnings</span>
          <strong>{fmtCurrency(totalDisplay, currency)}</strong>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// SECTION 4 — Your Target (collapsed)
// ---------------------------------------------------------------------------

function TargetSection({ detail, displayAmount, currency, viewCurrency, toLocal }) {
  const target = detail?.earning_sections_target;
  if (!target) return null;

  const ote = numeric(target.ote);
  const sources = (target.sources || []).filter(s => s.target_pool != null || s.actual != null);
  if (ote == null && !sources.length) return null;

  return (
    <CollapsibleSection eyebrow="Your Target">
      {ote != null && (
        <div className="pep__entitlement-ote">
          <span>On-Target Earnings (OTE)</span>
          <span className="pep__entitlement-ote-value">
            {fmtCurrency(displayAmount(ote), currency)}
          </span>
        </div>
      )}
      {sources.length > 0 && (
        <div className="pep__decomp-table">
          <div className="pep__decomp-header">
            <span>Earning source</span>
            <span>Weight</span>
            <span>Target earnings</span>
            <span>Actual payout</span>
          </div>
          {sources.map(s => {
            const pool   = numeric(s.target_pool);
            const actual = numeric(s.actual);
            const poolD  = pool   != null ? displayAmount(pool)   : null;
            const actD   = actual != null ? displayAmount(actual) : null;
            const over   = poolD != null && actD != null && actD > poolD;
            return (
              <div key={s.source_key} className="pep__decomp-row">
                <span className="pep__decomp-component">{s.display_name}</span>
                <span className="pep__decomp-weight">
                  {s.weight != null ? fmtPct(s.weight) : 'Not applicable'}
                </span>
                <span className="pep__decomp-pool">{fmtCurrency(poolD, currency)}</span>
                <span className={`pep__decomp-actual${over ? ' pep__decomp-actual--over' : ''}`}>
                  {fmtCurrency(actD, currency)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </CollapsibleSection>
  );
}

// ---------------------------------------------------------------------------
// SECTION 5 — Plan Details (collapsed)
// ---------------------------------------------------------------------------

function PlanDetailsSection({ plan }) {
  if (!plan) return null;
  return (
    <CollapsibleSection eyebrow="Plan Details">
      <div className="pep__plan-detail">
        <p className="pep__plan-name">{plan.planName || plan.planCode || '—'}</p>
        {plan.planCode && <p className="pep__plan-code">{plan.planCode}</p>}
        <div className="pep__plan-meta-row">
          {plan.planVersionLabel && (
            <span className="pep__version-pill">{plan.planVersionLabel}</span>
          )}
          {plan.temporalRelationship === 'governs_current' && plan.effectiveFrom && (
            <p className="pep__plan-governs">
              Governs current period · from {plan.effectiveFrom}
            </p>
          )}
        </div>
        {(plan.submittedBy || plan.approvedBy) && (
          <div className="pep__approval-chain">
            {plan.submittedBy?.name && (
              <div className="pep__approval-actor">
                <span className="pep__approval-role">Submitted by</span>
                <span className="pep__approval-name">{plan.submittedBy.name}</span>
                {plan.submittedBy.at && (
                  <span className="pep__approval-date">{fmtDate(plan.submittedBy.at)}</span>
                )}
              </div>
            )}
            {plan.approvedBy?.name && (
              <div className="pep__approval-actor">
                <span className="pep__approval-role">Approved by</span>
                <span className="pep__approval-name">{plan.approvedBy.name}</span>
                {plan.approvedBy.at && (
                  <span className="pep__approval-date">{fmtDate(plan.approvedBy.at)}</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

/**
 * S1044-INLINE: `inline` prop — renders PEP content directly into the page flow
 * instead of as a fixed right-side slide-in panel.  Used by ManagerShell MyPayout
 * to replace the legacy custom components[] rendering without duplicating any logic.
 *
 * When inline=true:
 *   • pep__backdrop and pep__panel wrappers are suppressed
 *   • drag handle, close button, and Escape key listener are suppressed
 *   • content is rendered in a plain <div> with class "pep__inline-root"
 *   • currency toggle, performance tiles, proof chain etc. all work unchanged
 *   • onClose prop is optional (defaults to no-op) — not shown to user
 *
 * ROLLBACK: git revert this file to the commit before S1044-INLINE.
 * The legacy ManagerShell components[] rendering was removed in ManagerShell.jsx
 * simultaneously — revert both files together if rollback is needed.
 */
function PayoutExplanationPanel({
  payoutSummaryId,
  positionName,
  roleCode,
  periodLabel,
  planName,
  planVersion,
  viewerRole,
  onClose,
  inline = false,
}) {
  const { detail, loading, error } = usePayoutDetail(payoutSummaryId);
  const closeRef = useRef(null);

  const { panelWidth, dragHandleProps } = useResizablePanel({
    storageKey: 'df_panel_payout', defaultWidth: 680, minWidth: 380, maxWidth: 1100,
  });

  useEffect(() => {
    if (payoutSummaryId && closeRef.current) closeRef.current.focus();
  }, [payoutSummaryId]);

  useEffect(() => {
    // S1044-INLINE: Escape key only applies to the slide-in panel mode.
    if (inline || !payoutSummaryId) return;
    function handleKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [payoutSummaryId, onClose, inline]);

  // Currency state
  const [viewCurrency, setViewCurrency] = useState('local');

  const localCurrencyCode = detail?.local_currency_code || null;
  const baseCurrencyCode  = detail?.currency_code || null;
  const fxRate            = detail?.fx_rate ? Number(detail.fx_rate) : null;
  const earningModel      = detail?.earning_model ?? 'individual';
  const earningSections   = detail?.earning_sections ?? [];

  const isPureRollup  = earningModel === 'rollup' || earningModel === 'leadership_rollup';
  const isPlayerCoach = earningModel === 'player_coach';

  const showToggle = (
    localCurrencyCode !== null &&
    baseCurrencyCode  !== null &&
    localCurrencyCode !== baseCurrencyCode &&
    detail?.total_payout_local != null &&
    viewerRole !== 'rep' &&
    !isPureRollup
  );

  const isCrossCurrencyRollup = isPureRollup && localCurrencyCode === null;

  useEffect(() => {
    if (viewerRole === 'rep') {
      setViewCurrency('local');
    } else if (isCrossCurrencyRollup) {
      setViewCurrency('base');
    } else if (!localCurrencyCode || localCurrencyCode === baseCurrencyCode) {
      setViewCurrency('base');
    }
  }, [viewerRole, isCrossCurrencyRollup, localCurrencyCode, baseCurrencyCode]);

  const currency = viewCurrency === 'local' && localCurrencyCode
    ? localCurrencyCode : baseCurrencyCode;

  function toLocal(baseAmount) {
    if (baseAmount == null) return null;
    const n = Number(baseAmount);
    if (!Number.isFinite(n)) return null;
    if (!localCurrencyCode || localCurrencyCode === baseCurrencyCode) return n;
    if (!fxRate || fxRate === 0) return null;
    return n / fxRate;
  }

  function displayAmount(baseAmount) {
    const n = Number(baseAmount);
    if (!Number.isFinite(n)) return null;
    return viewCurrency !== 'local' ? n : toLocal(n);
  }

  const totalPayout = viewCurrency === 'local' && detail?.total_payout_local != null
    ? detail.total_payout_local : detail?.total_payout;

  const headerMeta = [roleCode, periodLabel, planName, planVersion]
    .filter(Boolean).join(' · ');

  if (!payoutSummaryId) return null;

  // ---------------------------------------------------------------------------
  // S1044-INLINE: Shared header content — currency toggle + meta line.
  // Rendered inside either the slide-in panel header or the inline root header.
  // ---------------------------------------------------------------------------
  const sharedHeader = (
    <div className="pep__header-row2">
      <p className="pep__header-meta" title={headerMeta}>{headerMeta}</p>
      {showToggle && (
        <div className="pep__currency-toggle" role="group" aria-label="View amounts in">
          <button type="button"
            className={`pep__currency-btn${viewCurrency === 'local' ? ' pep__currency-btn--active' : ''}`}
            onClick={() => setViewCurrency('local')}
            aria-label={`${localCurrencyCode} — Local currency`}>
            <span className="pep__currency-btn-code">{localCurrencyCode}</span>
            <span className="pep__currency-btn-context"> · Local</span>
          </button>
          <button type="button"
            className={`pep__currency-btn${viewCurrency === 'base' ? ' pep__currency-btn--active' : ''}`}
            onClick={() => setViewCurrency('base')}
            aria-label={`${baseCurrencyCode} — Company currency`}>
            <span className="pep__currency-btn-code">{baseCurrencyCode}</span>
            <span className="pep__currency-btn-context"> · Company</span>
          </button>
        </div>
      )}
      {isCrossCurrencyRollup && (
        <span className="pep__currency-static">{baseCurrencyCode} · Company</span>
      )}
    </div>
  );

  // ---------------------------------------------------------------------------
  // S1044-INLINE: Shared body content — used by both inline and panel modes.
  // Defined once here so neither branch duplicates JSX.
  // ---------------------------------------------------------------------------
  const bodyContent = (
    <>
      {loading && (
        <div className="pep__loading" aria-busy="true">
          <div className="skeleton pep__skeleton--tall" />
          <div className="skeleton pep__skeleton" />
          <div className="skeleton pep__skeleton" />
        </div>
      )}
      {error && !loading && (
        <div className="pep__error" role="alert">
          <p className="pep__error-title">Unable to load payout detail</p>
          <p className="pep__error-message">{error.message}</p>
        </div>
      )}
      {detail && !loading && (
        <>
          {/* SECTION 1 — Payout Summary */}
          <section className="pep__section pep__section--summary">
            <div className="pep__payout-hero">
              <span>Total payout</span>
              <strong>{fmtCurrency(totalPayout, currency)}</strong>
            </div>

            <OteAnchorStrip
              detail={detail}
              displayAmount={displayAmount}
              currency={currency}
              earningSections={earningSections}
              earningModel={earningModel}
            />

            <PerformanceTiles
              detail={detail}
              displayAmount={displayAmount}
              currency={currency}
              baseCurrencyCode={baseCurrencyCode}
              earningModel={earningModel}
              toLocal={toLocal}
              viewCurrency={viewCurrency}
            />

            {detail.is_latest_final != null && (
              <div className="pep__run-badge-row">
                <span className={`pep__run-badge pep__run-badge--${detail.is_latest_final ? 'final' : 'provisional'}`}>
                  {detail.is_latest_final ? 'Final' : 'Provisional'}
                </span>
                {detail.calculated_at && (
                  <span className="pep__run-date">Calculated {fmtDate(detail.calculated_at)}</span>
                )}
              </div>
            )}

            {showToggle && fxRate != null && (
              <p className="pep__fx-disclosure">
                FX rate used: 1 {localCurrencyCode} = {detail.fx_rate} {baseCurrencyCode} · Approved prior-close rate
              </p>
            )}
          </section>

          {/* SECTION 2 — Reconciliation */}
          <ReconciliationSection
            detail={detail}
            totalPayout={totalPayout}
            currency={currency}
            toLocal={toLocal}
            viewCurrency={viewCurrency}
          />

          {/* SECTION 3 — Earning sections (canonical contract) */}
          {earningSections.map(section => (
            <EarningSection
              key={section.section_key}
              section={section}
              currency={currency}
              viewCurrency={viewCurrency}
              toLocal={toLocal}
              displayAmount={displayAmount}
              baseCurrencyCode={baseCurrencyCode}
              localCurrencyCode={localCurrencyCode}
            />
          ))}

          {/* SECTION 4 — Your Target */}
          <TargetSection
            detail={detail}
            displayAmount={displayAmount}
            currency={currency}
            viewCurrency={viewCurrency}
            toLocal={toLocal}
          />

          {/* SECTION 5 — Plan Details */}
          <PlanDetailsSection plan={detail.planContext} />
        </>
      )}
    </>
  );

  // ---------------------------------------------------------------------------
  // S1044-INLINE: Inline mode — no backdrop, no fixed panel, no close button.
  // Renders content directly into page flow for ManagerShell MyPayout section.
  // ---------------------------------------------------------------------------
  if (inline) {
    return (
      <div className="pep__inline-root">
        {(positionName || showToggle) && (
          <header className="pep__header pep__header--inline">
            {positionName && (
              <div className="pep__header-row1">
                <p className="pep__header-name">{positionName}</p>
              </div>
            )}
            {sharedHeader}
          </header>
        )}
        <div className="pep__body pep__body--inline">
          {bodyContent}
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Default: slide-in panel mode (unchanged — existing callers unaffected).
  // ---------------------------------------------------------------------------
  return (
    <>
      <div className="pep__backdrop" role="presentation"
        onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }} />

      <div className="pep__panel" role="dialog" aria-modal="true"
        aria-label={`Payout explanation — ${positionName ?? periodLabel}`}
        tabIndex={-1} style={{ width: panelWidth }}>

        <div {...dragHandleProps} />

        {/* Sticky header */}
        <header className="pep__header">
          <div className="pep__header-row1">
            <p className="pep__header-name">{positionName ?? '—'}</p>
            <button ref={closeRef} type="button" className="icon-button pep__close"
              aria-label="Close payout explanation" onClick={onClose}>×</button>
          </div>
          {sharedHeader}
        </header>

        {/* Body — S1044-INLINE: bodyContent variable shared with inline branch above */}
        <div className="pep__body">
          {bodyContent}
        </div>
      </div>
      <style>{styles}</style>
    </>
  );
}

PayoutExplanationPanel.propTypes = {
  payoutSummaryId: PropTypes.string,
  positionName:    PropTypes.string,
  roleCode:        PropTypes.string,
  periodLabel:     PropTypes.string,
  planName:        PropTypes.string,
  planVersion:     PropTypes.string,
  viewerRole:      PropTypes.oneOf(['rep', 'manager', 'comp_admin']),
  // S1044-INLINE: onClose not required when inline=true (no close button rendered).
  onClose:         PropTypes.func,
  // S1044-INLINE: when true, renders inline in page flow instead of as a slide-in panel.
  inline:          PropTypes.bool,
};

PayoutExplanationPanel.defaultProps = {
  viewerRole: 'comp_admin',
  inline:     false,
  onClose:    () => {},
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = `
  .pep__backdrop {
    position:fixed; inset:0; z-index:900;
    background:rgb(0 0 0/0.35);
  }
  .pep__panel {
    position:fixed; top:0; right:0; z-index:901;
    height:100%; display:flex; flex-direction:column;
    background:rgb(var(--color-bg));
    border-left:1px solid rgb(var(--color-border));
    box-shadow:-8px 0 32px rgb(0 0 0/0.14);
  }
  /* S1044-INLINE: inline root — no panel chrome, content flows in page */
  .pep__inline-root {
    display:flex; flex-direction:column; width:100%;
  }
  .pep__header--inline {
    position:relative; /* not sticky — inline mode has no scroll container */
    border-radius:12px 12px 0 0;
  }
  .pep__body--inline {
    flex:1; overflow:visible; /* no scroll clipping in page flow */
    padding:0;
  }
  /* Drag handle */
  .pep__panel > div:first-child {
    position:absolute; left:0; top:0; bottom:0; width:4px; cursor:col-resize;
  }
  /* Header */
  .pep__header {
    position:sticky; top:0; z-index:1;
    display:flex; flex-direction:column; gap:0;
    border-bottom:1px solid rgb(var(--color-border));
    background:rgb(var(--color-surface)); flex-shrink:0;
  }
  .pep__header-row1 {
    display:flex; align-items:center; justify-content:space-between;
    padding:10px 20px 4px; min-height:36px; gap:8px;
  }
  .pep__header-name {
    font-size:15px; font-weight:700; color:rgb(var(--color-text));
    margin:0; min-width:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
  }
  .pep__close { font-size:18px; line-height:1; }
  .pep__header-row2 {
    display:flex; align-items:center; justify-content:space-between;
    padding:0 20px 10px; min-height:28px; gap:8px;
  }
  .pep__header-meta {
    font-size:11px; color:rgb(var(--color-text-muted));
    white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
    min-width:0; flex:1;
  }
  /* Currency toggle */
  .pep__currency-toggle {
    display:flex; align-items:center; gap:2px; flex-shrink:0;
    border:1px solid rgb(var(--color-border)); border-radius:6px; overflow:hidden;
  }
  .pep__currency-btn {
    padding:2px 8px; font-size:11px; font-weight:600;
    background:none; border:none; cursor:pointer; color:rgb(var(--color-text-muted));
    white-space:nowrap;
  }
  .pep__currency-btn--active { background:rgb(var(--color-primary)); color:white; }
  .pep__currency-static { font-size:11px; font-weight:600; color:rgb(var(--color-text-muted)); flex-shrink:0; }
  /* Body */
  .pep__body { flex:1; overflow-y:auto; padding:0; display:flex; flex-direction:column; }
  /* Sections */
  .pep__section { padding:16px 20px; border-bottom:1px solid rgb(var(--color-border)); }
  .pep__section--summary { padding-bottom:12px; }
  .pep__eyebrow {
    font-size:10px; font-weight:700; letter-spacing:.06em;
    text-transform:uppercase; color:rgb(var(--color-text-muted)); margin:0 0 10px;
  }
  .pep__section-total {
    font-size:16px; font-weight:700; font-variant-numeric:tabular-nums;
    white-space:nowrap; flex-shrink:0;
  }
  /* Collapsible */
  .pep__section--collapsible { padding:0; }
  .pep__section-toggle {
    display:flex; align-items:center; justify-content:space-between;
    width:100%; background:none; border:none; cursor:pointer;
    padding:14px 20px; text-align:left;
    border-bottom:1px solid rgb(var(--color-border));
  }
  .pep__section-toggle .pep__eyebrow { margin:0; }
  .pep__section-chevron { font-size:10px; color:rgb(var(--color-text-muted)); flex-shrink:0; }
  .pep__section-body { padding:14px 20px 16px; }
  /* Hero */
  .pep__payout-hero {
    display:flex; align-items:baseline; justify-content:space-between;
    gap:16px; margin:2px 0 14px; padding:10px 12px;
    border:1px solid var(--border-subtle,#d7dce3); border-radius:8px;
  }
  .pep__payout-hero span { font-size:12px; font-weight:600; }
  .pep__payout-hero strong { font-size:22px; line-height:1; }
  /* Tiles */
  .pep__summary-grid { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:8px; margin-bottom:12px; }
  .pep__dual-performance,.pep__performance-card { display:grid; gap:10px; }
  .pep__performance-card-title { display:flex; justify-content:space-between; align-items:baseline; gap:8px; font-size:11px; font-weight:700; letter-spacing:.03em; }
  .pep__performance-card-title small { font-weight:500; letter-spacing:0; }
  .pep__performance-four { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:8px; }
  .pep__metric { border:1px solid rgb(var(--color-border)); border-radius:8px; padding:10px 12px; display:flex; flex-direction:column; gap:3px; }
  .pep__metric-label { font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:.04em; color:rgb(var(--color-text-muted)); }
  .pep__metric-value { font-size:14px; font-weight:700; font-variant-numeric:tabular-nums; color:rgb(var(--color-text)); }
  .pep__metric-sub { font-size:10px; color:rgb(var(--color-text-muted)); }
  .pep__metric--rates { gap:4px; }
  .pep__rate-tile-row { display:flex; align-items:baseline; gap:6px; font-size:11px; }
  .pep__rate-tile-component { color:rgb(var(--color-text-muted)); min-width:32px; flex-shrink:0; }
  .pep__rate-tile-rate { font-weight:700; font-variant-numeric:tabular-nums; }
  .pep__rate-tile-state { font-size:10px; color:rgb(var(--color-text-muted)); }
  /* Run badge */
  .pep__run-badge-row { display:flex; align-items:center; gap:8px; margin-bottom:6px; }
  .pep__run-badge { font-size:10px; font-weight:700; padding:2px 8px; border-radius:999px; letter-spacing:.04em; }
  .pep__run-badge--final { background:rgb(var(--color-success)/0.1); color:rgb(var(--color-success)); border:1px solid rgb(var(--color-success)/0.3); }
  .pep__run-badge--provisional { background:rgb(var(--color-warning)/0.1); color:rgb(var(--color-warning)); border:1px solid rgb(var(--color-warning)/0.3); }
  .pep__run-date { font-size:11px; color:rgb(var(--color-text-muted)); }
  .pep__fx-disclosure { font-size:10px; color:rgb(var(--color-text-muted)); margin:2px 0 0; }
  /* OTE strip */
  .pep__ote-strip { display:flex; align-items:center; gap:8px; padding:10px 14px; margin-bottom:10px; background:rgb(var(--color-surface-muted)); border:1px solid rgb(var(--color-border)); border-radius:8px; }
  .pep__ote-strip-cell { display:flex; flex-direction:column; align-items:flex-start; flex:1; }
  .pep__ote-strip-label { font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:rgb(var(--color-text-muted)); margin-bottom:2px; }
  .pep__ote-strip-value { font-size:14px; font-weight:700; font-variant-numeric:tabular-nums; color:rgb(var(--color-text)); }
  .pep__ote-strip-note { font-size:10px; color:rgb(var(--color-text)); opacity:.65; margin-top:2px; font-style:italic; }
  .pep__ote-strip-op { font-size:16px; font-weight:400; color:rgb(var(--color-text-muted)); flex-shrink:0; padding:0 2px; }
  /* Reconciliation */
  .pep__reconcile { display:flex; flex-direction:column; gap:2px; }
  .pep__reconcile-row { display:flex; align-items:baseline; justify-content:space-between; gap:12px; padding:5px 10px; font-size:13px; border-radius:6px; color:rgb(var(--color-text)); }
  .pep__reconcile-amount { font-variant-numeric:tabular-nums; font-weight:500; white-space:nowrap; }
  .pep__reconcile-total { display:flex; align-items:baseline; justify-content:space-between; gap:12px; padding:8px 10px; margin-top:6px; border-top:2px solid rgb(var(--color-border)); font-size:14px; font-weight:700; }
  .pep__reconcile-total strong { font-size:17px; font-variant-numeric:tabular-nums; color:rgb(var(--color-primary)); }
  .pep__bridge-group + .pep__bridge-group { margin-top:8px; }
  .pep__bridge-group-title { margin:7px 0 3px; font-size:11px; font-weight:700; letter-spacing:.03em; text-transform:uppercase; }
  .pep__reconcile-row--detail { padding-left:8px; }
  .pep__reconcile-pct-ote { display:flex; justify-content:space-between; gap:12px; padding:4px 10px; font-size:11px; color:rgb(var(--color-text-muted)); font-style:italic; }
  .pep__integrity-warning { display:flex; flex-direction:column; gap:4px; margin-top:10px; padding:10px 12px; background:rgb(var(--color-warning)/0.08); border:1px solid rgb(var(--color-warning)/0.25); border-radius:6px; font-size:12px; }
  .pep__integrity-warning strong { color:rgb(var(--color-warning)); }
  /* Section header with total */
  .pep__component-header { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; margin-bottom:12px; }
  /* Personal performance block */
  .pep__personal-perf { margin-bottom:12px; }
  .pep__perf-row-block { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:8px; margin-bottom:6px; }
  .pep__perf-kv { display:flex; flex-direction:column; gap:2px; }
  .pep__perf-kv span { font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:.04em; color:rgb(var(--color-text-muted)); }
  .pep__perf-kv strong { font-size:13px; font-weight:700; font-variant-numeric:tabular-nums; }
  .pep__perf-kv strong small { display:block; font-size:10px; font-weight:500; color:rgb(var(--color-text-muted)); }
  .pep__quota-grain-note { font-size:11px; color:rgb(var(--color-text-muted)); margin:4px 0 0; font-style:italic; }
  /* Team performance block */
  .pep__team-basis { border:1px solid rgb(var(--color-border)); border-radius:8px; overflow:hidden; margin-bottom:8px; }
  .pep__team-basis-header { display:flex; align-items:center; justify-content:space-between; padding:8px 12px 4px; font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:rgb(var(--color-text-muted)); }
  .pep__currency-context { font-size:10px; font-weight:600; color:rgb(var(--color-text-muted)); padding:1px 6px; border:1px solid rgb(var(--color-border)); border-radius:4px; }
  .pep__team-basis-metrics { padding:4px 12px 10px; display:flex; flex-direction:column; gap:3px; }
  .pep__team-basis-summary { font-size:11px; color:rgb(var(--color-text-muted)); margin:4px 0 0; font-style:italic; }
  .pep__team-basis-hq-note { font-size:10px; color:rgb(var(--color-text-muted)); margin:6px 0 0; font-style:italic; line-height:1.5; }
  .pep__team-basis-row { display:flex; align-items:baseline; justify-content:space-between; font-size:12px; gap:12px; color:rgb(var(--color-text)); }
  .pep__team-basis-row--result { font-weight:700; color:rgb(var(--color-text)); margin-top:4px; padding-top:4px; border-top:1px solid rgb(var(--color-border)); }
  /* Currency transition */
  .pep__currency-transition { margin:10px 0 8px; padding:8px 12px; border-radius:6px; background:rgb(var(--color-surface-muted)); border:1px solid rgb(var(--color-border)); }
  .pep__currency-transition-label { display:block; font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:rgb(var(--color-primary)); margin-bottom:4px; }
  .pep__currency-transition-note { font-size:11px; color:rgb(var(--color-text-muted)); margin:0; }
  /* Proof chain */
  .pep__proof { display:flex; flex-direction:column; gap:2px; }
  .pep__proof-row { display:flex; align-items:baseline; justify-content:space-between; gap:12px; padding:5px 8px; border-radius:5px; font-size:12px; color:rgb(var(--color-text)); }
  .pep__proof-row--result { background:rgb(var(--color-primary)/0.08); color:rgb(var(--color-primary)); font-weight:600; }
  .pep__proof-row--neg .pep__proof-value { color:rgb(var(--color-danger)); }
  .pep__proof-row--zero { opacity:.45; }
  .pep__proof-label { min-width:0; flex:1; }
  .pep__proof-note { font-size:11px; opacity:.7; font-weight:400; margin-left:4px; }
  .pep__proof-value { white-space:nowrap; font-variant-numeric:tabular-nums; font-weight:600; }
  .pep__proof-intro { font-size:11px; color:rgb(var(--color-text-muted)); font-style:italic; margin:6px 8px 4px; padding:0; }
  /* RTD section */
  .pep__rtd-section { margin-top:8px; }
  .pep__rtd-row { border-bottom:1px solid rgb(var(--color-border)/0.5); }
  .pep__rtd-type { font-weight:600; color:rgb(var(--color-text)); margin-right:4px; }
  .pep__rtd-detail { font-size:11px; color:rgb(var(--color-text-muted)); font-weight:400; margin-left:4px; }
  /* Source total */
  .pep__source-total { display:flex; justify-content:space-between; gap:12px; margin-top:10px; padding-top:9px; border-top:1px solid var(--border-subtle,#d7dce3); font-size:12px; }
  /* Your Target */
  .pep__entitlement-ote { display:flex; align-items:baseline; justify-content:space-between; gap:12px; margin-bottom:12px; font-size:13px; font-weight:600; }
  .pep__entitlement-ote-value { font-variant-numeric:tabular-nums; }
  .pep__decomp-table { display:flex; flex-direction:column; gap:4px; }
  .pep__decomp-header,.pep__decomp-row { display:grid; grid-template-columns:1fr 60px 100px 100px; gap:8px; padding:4px 0; }
  .pep__decomp-header { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:rgb(var(--color-text-muted)); border-bottom:1px solid rgb(var(--color-border)); padding-bottom:6px; }
  .pep__decomp-row { font-size:12px; }
  .pep__decomp-component { font-weight:600; }
  .pep__decomp-weight,.pep__decomp-pool,.pep__decomp-actual { font-variant-numeric:tabular-nums; text-align:right; }
  .pep__decomp-actual--over { color:rgb(var(--color-success)); }
  /* Plan details */
  .pep__plan-detail { display:flex; flex-direction:column; gap:6px; }
  .pep__plan-name { font-size:13px; font-weight:600; margin:0; }
  .pep__plan-code { font-size:11px; color:rgb(var(--color-text-muted)); margin:0; }
  .pep__plan-meta-row { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
  .pep__version-pill { font-size:10px; font-weight:700; padding:1px 8px; border:1px solid rgb(var(--color-primary)/0.35); border-radius:999px; background:rgb(var(--color-primary)/0.06); color:rgb(var(--color-primary)); }
  .pep__plan-governs { font-size:11px; color:rgb(var(--color-success)); margin:0; }
  .pep__approval-chain { display:flex; flex-direction:column; gap:4px; margin-top:4px; }
  .pep__approval-actor { display:flex; align-items:baseline; gap:6px; font-size:11px; }
  .pep__approval-role { color:rgb(var(--color-text-muted)); }
  .pep__approval-name { font-weight:600; }
  .pep__approval-date { color:rgb(var(--color-text-muted)); }
  /* Per-BU quota grain breakdown (S998) */
  .pep__grain-breakdown { margin:8px 0 4px; border:1px solid rgb(var(--color-border)); border-radius:8px; overflow:hidden; }
  .pep__grain-breakdown-toggle { display:flex; align-items:center; justify-content:space-between; width:100%; padding:8px 12px; background:rgb(var(--color-surface-muted)); border:none; cursor:pointer; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:rgb(var(--color-text-muted)); gap:8px; }
  .pep__grain-breakdown-toggle:hover { background:rgb(var(--color-border)/0.4); }
  .pep__grain-breakdown-chevron { font-size:9px; flex-shrink:0; }
  .pep__grain-breakdown-table { padding:4px 0; }
  .pep__grain-breakdown-header,.pep__grain-breakdown-row { display:grid; grid-template-columns:1fr 90px 120px 80px; gap:8px; padding:4px 12px; }
  .pep__grain-breakdown-header { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:rgb(var(--color-text-muted)); border-bottom:1px solid rgb(var(--color-border)); padding-bottom:6px; }
  .pep__grain-breakdown-row { font-size:12px; font-variant-numeric:tabular-nums; }
  .pep__grain-breakdown-row:not(:last-child) { border-bottom:1px solid rgb(var(--color-border)/0.4); }
  .pep__grain-bu-name { font-weight:600; color:rgb(var(--color-text)); }
  /* Loading / error */
  .pep__loading { padding:20px; display:flex; flex-direction:column; gap:12px; }
  .pep__skeleton { height:18px; border-radius:4px; }
  .pep__skeleton--tall { height:60px; border-radius:8px; }
  .pep__error { padding:20px; }
  .pep__error-title { font-weight:600; color:rgb(var(--color-danger)); margin:0 0 4px; }
  .pep__error-message { font-size:12px; color:rgb(var(--color-text-muted)); margin:0; }
  /* Responsive */
  @media (max-width:720px) {
    .pep__panel { width:100%!important; }
    .pep__summary-grid,.pep__performance-four,.pep__perf-row-block { grid-template-columns:repeat(2,minmax(0,1fr)); }
    .pep__decomp-header,.pep__decomp-row { grid-template-columns:1fr 50px 80px 80px; }
    .pep__ote-strip { flex-wrap:wrap; gap:6px; }
    .pep__ote-strip-cell { flex:0 0 calc(50% - 8px); }
  }
  @media (max-width:500px) {
    .pep__currency-btn-context { display:none; }
    .pep__ote-strip-op { display:none; }
  }
`;

export default PayoutExplanationPanel;
